import React, { useMemo, useState, useEffect, useRef, useCallback } from 'react';
import { jsPDF } from 'jspdf';
import { FileDown, Users, Target, Hash, MapPin, TrendingUp, Search, Layers, AlertCircle, RefreshCw, Printer, LifeBuoy } from 'lucide-react';
import { supabase, forceReconnect } from '../lib/supabase';
import { useAuthStore } from '../store/authStore';
import { useNavigate } from 'react-router-dom';

interface RecommendationDisplayProps {
  recommendations: string;
  key?: string;
  onAfterExport?: () => void;
  decrementRequestCount?: boolean;
  onRequestRetry?: () => Promise<string>;
  isCalendarMode?: boolean;
}

const SECTION_TITLES = [
  '1. Persona Ideal',
  '2. Palavras-chave para Google Ads',
  '3. Interesses para Meta Ads',
  '4. Dados Demográficos',
  '5. Funis e Ofertas',
  '6. Dicas de SEO',
  '7. Recomendações de Investimento'
];

const CALENDAR_TITLE = 'Calendário de Conteúdo';

const SECTION_ICONS = [Users, Hash, Target, MapPin, Layers, Search, TrendingUp];

const HEADER_IMAGES = [
  'https://images.unsplash.com/photo-1600880292203-757bb62b4baf?ixlib=rb-1.2.1&auto=format&fit=crop&w=1200&q=80',
  'https://images.unsplash.com/photo-1676276375773-add2cbdd1cc5?q=80&w=1470&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
  'https://images.unsplash.com/photo-1711097383282-28097ae16b1d?q=80&w=1470&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
  'https://images.unsplash.com/photo-1709281847780-2b34c28853c0?q=80&w=1470&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
  'https://images.unsplash.com/photo-1709715357528-1d3d78ed6cfc?q=80&w=1471&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
  'https://images.unsplash.com/photo-1709715357549-f2d587846ee1?q=80&w=1471&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
  'https://images.unsplash.com/photo-1577962917302-cd874c4e31d2?q=80&w=1632&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D'
];

const EXPORT_BUTTON_VISIBILITY_KEY = 'auraAI-export-button-visible';
const TAB_SWITCH_DETECTED_KEY = 'auraAI-tab-switch-detected';
const EXPORT_TIMEOUT = 4000;
const MAX_RETRY_ATTEMPTS = 50;
const RETRY_DELAY = 1500;

const processEmojisForPDF = (text: string): string => {
  return text
    .replace(/📊|📈|📉/g, "(gráfico)")
    .replace(/🎯/g, "(alvo)")
    .replace(/🔍/g, "(busca)")
    .replace(/📱/g, "(celular)")
    .replace(/💰/g, "(dinheiro)")
    .replace(/📝/g, "(nota)")
    .replace(/🚀/g, "(foguete)")
    .replace(/⭐/g, "(estrela)")
    .replace(/✅/g, "(verificado)")
    .replace(/❤️/g, "(coração)")
    .replace(/👉/g, "->")
    .replace(/📞/g, "(telefone)")
    .replace(/Ø=D€/g, "Oferecemos")
    .replace(/Ø=ÚÈ/g, "Entre em contato")
    .replace(/r #/g, "seu negócio #")
    .replace(/[\u{25A0}-\u{25FF}]/gu, "■")
    .replace(/[\u{2600}-\u{26FF}]/gu, "•")
    .replace(/[\u{1F300}-\u{1F5FF}]/gu, "•")
    .replace(/[\u{1F600}-\u{1F64F}]/gu, "•")
    .replace(/[\u{1F680}-\u{1F6FF}]/gu, "•")
    .replace(/[\u{2700}-\u{27BF}]/gu, "•")
    .replace(/[^\x00-\x7F]/g, function(char) {
      if (/[\u24B6-\u24E9]/i.test(char)) {
        return String.fromCharCode(char.charCodeAt(0) - 9333);
      } else if (/[\u24D0-\u24E9]/i.test(char)) {
        return String.fromCharCode(char.charCodeAt(0) - 9327);
      } else if (/[\u2460-\u2473]/i.test(char)) {
        return String.fromCharCode(char.charCodeAt(0) - 9312 + 48);
      } else if (/[À-ÿ]/.test(char)) {
        return char;
      } else if (char === 'ç' || char === 'Ç') {
        return char;
      } else {
        return ' ';
      }
    });
};

export const RecommendationDisplay: React.FC<RecommendationDisplayProps> = ({ 
  recommendations, 
  onAfterExport,
  decrementRequestCount = true,
  onRequestRetry,
  isCalendarMode = false
}) => {
  const navigate = useNavigate();
  const [isExporting, setIsExporting] = useState(false);
  const [sessionCheckInProgress, setSessionCheckInProgress] = useState(false);
  const [exportError, setExportError] = useState('');
  const [componentKey, setComponentKey] = useState(() => Math.random().toString(36).substring(2, 15));
  const [reconnecting, setReconnecting] = useState(false);
  const [isTabVisible, setIsTabVisible] = useState(true);
  const [showFallbackButton, setShowFallbackButton] = useState(false);
  const [exportTimeoutId, setExportTimeoutId] = useState<NodeJS.Timeout | null>(null);
  const [showExportButton, setShowExportButton] = useState(() => {
    try {
      const savedState = localStorage.getItem(EXPORT_BUTTON_VISIBILITY_KEY);
      const tabSwitchDetected = localStorage.getItem(TAB_SWITCH_DETECTED_KEY);
      return savedState === null || savedState === 'true';
    } catch (e) {
      return true;
    }
  });
  const [requestCountUpdated, setRequestCountUpdated] = useState(false);
  const [currentRecommendations, setCurrentRecommendations] = useState(recommendations);
  const [isRetrying, setIsRetrying] = useState(false);
  const [retryAttempts, setRetryAttempts] = useState(0);
  const [retryProgress, setRetryProgress] = useState(0);
  const [autoRetryEnabled, setAutoRetryEnabled] = useState(true);
  const [isFetchingRecommendations, setIsFetchingRecommendations] = useState(false);
  const [hasExported, setHasExported] = useState(false);
  
  const retryTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isRetryingRef = useRef(false);
  const retryAttemptsRef = useRef(0);
  const autoRetryInProgressRef = useRef(false);
  const lastRetryTimeRef = useRef(0);
  const mountedRef = useRef(true);
  const domNodesRef = useRef<{[key: string]: HTMLElement | null}>({});

  const { subscription, checkAndRefreshSession } = useAuthStore();
  const exportingRef = useRef(false);
  const exportAbortControllerRef = useRef<AbortController | null>(null);
  const printContentRef = useRef<HTMLDivElement>(null);

  const selectedHeaderImage = useMemo(() => {
    const randomIndex = Math.floor(Math.random() * HEADER_IMAGES.length);
    return HEADER_IMAGES[randomIndex];
  }, [componentKey]);

  const showLimitReached = (subscription?.plan === 'essential' || subscription?.plan === 'initial') && 
                         ((subscription?.request_count ?? 0) <= 0 || 
                          (subscription?.next_billing_date && 
                           new Date(new Date(subscription.next_billing_date).getTime() + 24 * 60 * 60 * 1000) < new Date()));

  const updateExportButtonVisibility = (isVisible: boolean) => {
    setShowExportButton(isVisible);
    try {
      localStorage.setItem(EXPORT_BUTTON_VISIBILITY_KEY, isVisible ? 'true' : 'false');
    } catch (e) {
      console.warn('Failed to save button state:', e);
    }
  };

  const clearExportTimeout = () => {
    if (exportTimeoutId) {
      clearTimeout(exportTimeoutId);
      setExportTimeoutId(null);
    }
  };

  useEffect(() => {
    const handleVisibilityChange = () => {
      const isVisible = document.visibilityState === 'visible';
      setIsTabVisible(isVisible);
      if (!isVisible) {
        updateExportButtonVisibility(false);
        try {
          localStorage.setItem(TAB_SWITCH_DETECTED_KEY, 'true');
        } catch (e) {
          console.warn('Failed to save tab state:', e);
        }
      }
      if (isVisible) {
        updateExportButtonVisibility(false);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

const extractSections = (content: string) => {
  const sections: {title: string, content: string, icon: React.ElementType}[] = [];
  if (!content) return sections;
  
  // Verificar se é um calendário
  const calendarIndex = content.indexOf(CALENDAR_TITLE);
  if (calendarIndex >= 0) {
    // Extrair todo o conteúdo após o título do calendário
    const calendarContent = content.slice(calendarIndex).trim();
    return [{
      title: CALENDAR_TITLE,
      content: calendarContent,
      icon: Layers
    }];
  }

  // Processamento normal para segmentação
  let remainingContent = content;
  SECTION_TITLES.forEach((title, index) => {
    const startIdx = remainingContent.indexOf(title);
    if (startIdx === -1) return;

    const nextTitle = SECTION_TITLES[index + 1];
    const endIdx = nextTitle 
      ? remainingContent.indexOf(nextTitle) 
      : remainingContent.length;

    const sectionContent = remainingContent
      .slice(startIdx, endIdx)
      .trim();

    sections.push({
      title: title.replace(/^\d+\.\s/, ''),
      content: sectionContent,
      icon: SECTION_ICONS[index]
    });

    remainingContent = remainingContent.slice(endIdx);
  });

  return sections;
};
  
  const sections = useMemo(() => extractSections(currentRecommendations), [currentRecommendations]);
  
  const allSectionsPresent = useMemo(() => {
  if (sections.length === 0) return false;
  // Se for calendário, só precisa ter 1 seção
  if (sections[0].title === CALENDAR_TITLE) return true;
  // Caso contrário, verifica todas as seções de segmentação
  return sections.length === SECTION_TITLES.length;
}, [sections]);

  const hardReset = useCallback(() => {
    if (autoRetryInProgressRef.current) {
      console.log('Ignorando hardReset durante retentativas automáticas');
      return;
    }
    
    if (exportAbortControllerRef.current) {
      try {
        exportAbortControllerRef.current.abort();
      } catch (e) {
        console.warn('Error aborting operation:', e);
      } finally {
        exportAbortControllerRef.current = null;
      }
    }
    
    exportingRef.current = false;
    clearExportTimeout();
    
    setTimeout(() => {
      if (mountedRef.current) {
        setIsExporting(false);
        setSessionCheckInProgress(false);
        setExportError('');
        setShowFallbackButton(false);
      }
    }, 50);
  }, []);

  const forceRefreshSession = async () => {
    try {
      setReconnecting(true);
      
      const cacheKeys = Object.keys(sessionStorage);
      for (const key of cacheKeys) {
        if (key.startsWith('supabase.auth') || key.startsWith('sb-')) {
          sessionStorage.removeItem(key);
        }
      }
      
      await checkAndRefreshSession();
      await forceReconnect();
      hardReset();
      
      return true;
    } catch (error) {
      console.error('Session refresh error:', error);
      return false;
    } finally {
      setReconnecting(false);
    }
  };

  const decrementRequestCounter = async () => {
    if (decrementRequestCount && 
        (subscription?.plan === 'essential' || subscription?.plan === 'initial') && 
        subscription?.request_count > 0 && 
        !requestCountUpdated) {
      try {
        const { error: updateError } = await supabase
          .from('subscriptions')
          .update({ request_count: subscription.request_count - 1 })
          .eq('user_id', subscription.user_id);

        if (updateError) {
          console.error('Error updating request count:', updateError);
          return false;
        }
        
        setRequestCountUpdated(true);
        return true;
      } catch (error) {
        console.error('Error in decrementRequestCounter:', error);
        return false;
      }
    }
    return true;
  };

const performAutoRetry = useCallback(async () => {
  if (autoRetryInProgressRef.current || !onRequestRetry || !mountedRef.current) {
    return;
  }

  if (retryAttemptsRef.current >= MAX_RETRY_ATTEMPTS) {
    console.log(`Limite de ${MAX_RETRY_ATTEMPTS} tentativas atingido. Parando retentativas automáticas.`);
    if (mountedRef.current) {
      setIsRetrying(false);
      setIsFetchingRecommendations(false);
      setRetryProgress(0);
      autoRetryInProgressRef.current = false;
    }
    return;
  }

  try {
    autoRetryInProgressRef.current = true;
    
    if (mountedRef.current) {
      setIsRetrying(true);
      setIsFetchingRecommendations(true);
      
      retryAttemptsRef.current += 1;
      setRetryAttempts(retryAttemptsRef.current);
      
      console.log(`Iniciando retentativa automática #${retryAttemptsRef.current} de ${MAX_RETRY_ATTEMPTS}`);
    }
    
    const progressInterval = 100;
    let currentProgress = 0;
    
    const progressTimer = setInterval(() => {
      if (mountedRef.current) {
        currentProgress += (progressInterval / RETRY_DELAY) * 100;
        setRetryProgress(Math.min(currentProgress, 99));
      } else {
        clearInterval(progressTimer);
      }
    }, progressInterval);
    
    await new Promise(resolve => {
      retryTimeoutRef.current = setTimeout(resolve, RETRY_DELAY);
    });
    
    clearInterval(progressTimer);
    
    if (mountedRef.current) {
      setRetryProgress(100);
    }
    
    lastRetryTimeRef.current = Date.now();
    
    if (!mountedRef.current) return;
    
    console.log(`Executando retentativa #${retryAttemptsRef.current}`);
    const newRecommendations = await onRequestRetry();
    
    if (newRecommendations && newRecommendations.length > 0) {
      const newSections = extractSections(newRecommendations);
      const isComplete = newSections[0]?.title === CALENDAR_TITLE || 
                        newSections.length === SECTION_TITLES.length;
      
      if (mountedRef.current) {
        setCurrentRecommendations(newRecommendations);
        
        if (isComplete) {
          console.log(`Retentativa #${retryAttemptsRef.current} bem-sucedida!`);
          // ALTERAÇÃO AQUI: Decrementar contador apenas quando bem-sucedida
          await decrementRequestCounter();
          retryAttemptsRef.current = 0;
          setRetryAttempts(0);
          setIsRetrying(false);
          setIsFetchingRecommendations(false);
          setRetryProgress(0);
          autoRetryInProgressRef.current = false;
        } else {
          console.log(`Retentativa #${retryAttemptsRef.current} retornou dados incompletos. Continuando...`);
          setTimeout(() => {
            if (mountedRef.current) {
              performAutoRetry();
            }
          }, 500);
        }
      }
    } else {
      if (mountedRef.current) {
        console.log(`Retentativa #${retryAttemptsRef.current} falhou. Tentando novamente...`);
        setTimeout(() => {
          if (mountedRef.current) {
            performAutoRetry();
          }
        }, 500);
      }
    }
  } catch (error) {
    console.error(`Erro na retentativa automática #${retryAttemptsRef.current}:`, error);
    
    if (mountedRef.current) {
      setTimeout(() => {
        if (mountedRef.current) {
          autoRetryInProgressRef.current = false;
          performAutoRetry();
        }
      }, 1000);
    }
  } finally {
    if (!mountedRef.current || retryAttemptsRef.current >= MAX_RETRY_ATTEMPTS) {
      autoRetryInProgressRef.current = false;
    }
  }
}, [onRequestRetry, decrementRequestCounter]);

  useEffect(() => {
    if (onRequestRetry && !allSectionsPresent && currentRecommendations && !autoRetryInProgressRef.current) {
      console.log('Iniciando sequência de retentativas automáticas');
      performAutoRetry();
    }
  }, [allSectionsPresent, currentRecommendations, performAutoRetry, onRequestRetry]);

useEffect(() => {
  if (recommendations && recommendations !== currentRecommendations) {
    console.log('Novas recomendações recebidas do componente pai');
    setCurrentRecommendations(recommendations);
    
    const newSections = extractSections(recommendations);
    const isComplete = newSections[0]?.title === CALENDAR_TITLE || 
                      newSections.length === SECTION_TITLES.length;
    
    if (isComplete) {
      console.log('Novas recomendações estão completas');
      // ALTERAÇÃO AQUI: Decrementar contador apenas quando completas
      decrementRequestCounter();
      retryAttemptsRef.current = 0;
      setRetryAttempts(0);
      setIsRetrying(false);
      setIsFetchingRecommendations(false);
      setRetryProgress(0);
      autoRetryInProgressRef.current = false;
    } else if (!autoRetryInProgressRef.current) {
      console.log('Novas recomendações estão incompletas, iniciando retentativas');
      performAutoRetry();
    }
  }
}, [recommendations, performAutoRetry, decrementRequestCounter]);

  // REMOVIDO: O useEffect que decrementava automaticamente

  useEffect(() => {
    checkAndRefreshSession();
  }, [checkAndRefreshSession]);

  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'hidden') {
        if (exportAbortControllerRef.current) {
          try {
            exportAbortControllerRef.current.abort();
          } catch (e) {
            console.warn('Error aborting operation:', e);
          }
        }
        clearExportTimeout();
        updateExportButtonVisibility(false);
        try {
          localStorage.setItem(TAB_SWITCH_DETECTED_KEY, 'true');
        } catch (e) {
          console.warn('Failed to save tab state:', e);
        }
      } else if (document.visibilityState === 'visible') {
        await checkAndRefreshSession();
        
        if (!autoRetryInProgressRef.current) {
          hardReset();
        }
        
        updateExportButtonVisibility(false);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [checkAndRefreshSession, hardReset]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      autoRetryInProgressRef.current = false;
      
      if (exportAbortControllerRef.current) {
        exportAbortControllerRef.current.abort();
      }
      clearExportTimeout();
      
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (currentRecommendations) {
      try {
        const tabSwitchDetected = localStorage.getItem(TAB_SWITCH_DETECTED_KEY);
        if (tabSwitchDetected === 'true') {
          updateExportButtonVisibility(false);
        }
      } catch (e) {
        console.warn('Failed to read tab state:', e);
      }
    }
  }, [currentRecommendations]);

  const verifySession = async (signal: AbortSignal): Promise<boolean> => {
    try {
      setSessionCheckInProgress(true);
      setExportError('');
      
      if (signal.aborted) throw new Error('Operation cancelled');
      
      await checkAndRefreshSession();
      
      let attempts = 0;
      let lastError = '';
      
      while (attempts < 3) {
        try {
          const { success, error } = await forceReconnect();
          
          if (signal.aborted) throw new Error('Operation cancelled');
          if (success) return true;
          
          lastError = error || 'Session verification failed';
        } catch (error) {
          lastError = error instanceof Error ? error.message : 'Unknown error';
        }
        
        attempts++;
        if (attempts < 3) {
          await new Promise(resolve => setTimeout(resolve, 1000 * attempts));
        }
      }
      
      throw new Error(lastError);
    } catch (error) {
      console.error('Session verification error:', error);
      
      if (!signal.aborted && mountedRef.current) {
        setExportError(error instanceof Error ? error.message : 'Session verification issue');
      }
      
      return false;
    } finally {
      if (mountedRef.current && !signal.aborted) {
        setSessionCheckInProgress(false);
      }
    }
  };

const printDocument = useCallback(() => {
  try {
    setExportError('');
    updateExportButtonVisibility(false);
    setShowFallbackButton(false);
    
    // Criar um iframe invisível em vez de usar window.open()
    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.right = '0';
    iframe.style.bottom = '0';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = '0';
    iframe.style.visibility = 'hidden';
    
    document.body.appendChild(iframe);
    
    const date = new Date().toLocaleDateString('pt-BR');
    const time = new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Relatório de Segmentação Aura AI - ${date}</title>
        <meta charset="utf-8">
        <style>
          body {
            font-family: 'Helvetica', 'Arial', sans-serif;
            margin: 0;
            padding: 20px;
            color: #333;
          }
          .header-image {
            width: 100%;
            height: auto;
            object-fit: cover;
            border-radius: 8px;
            margin-bottom: 20px;
          }
          .header {
            margin-bottom: 30px;
          }
          h1 {
            font-size: 24px;
            margin-bottom: 5px;
          }
          p {
            margin: 0 0 10px 0;
            line-height: 1.5;
          }
          .section {
            margin-bottom: 30px;
            break-inside: avoid;
          }
          .section-header {
            display: flex;
            align-items: center;
            margin-bottom: 15px;
          }
          .section-title {
            font-size: 18px;
            margin: 0 0 0 10px;
          }
          .heading {
            font-weight: bold;
            margin-top: 15px;
          }
          .content {
            margin: 5px 0;
          }
          @media print {
            body {
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
            .section {
              break-inside: avoid;
            }
          }
        </style>
      </head>
      <body>
        <img src="${selectedHeaderImage}" alt="Estratégia de marketing digital" class="header-image" />
        
        <div class="header">
          <h1>Relatório de Segmentação Aura AI</h1>
          <p>Gerado em ${date} às ${time}</p>
        </div>
        
        ${sections.map(section => `
          <div class="section">
            <div class="section-header">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                ${section.title === 'Persona Ideal' ? 
                  '<path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><path d="M23 21v-2a4 4 0 0 0-3-3.87"></path><path d="M16 3.13a4 4 0 0 1 0 7.75"></path>' : 
                section.title === 'Palavras-chave para Google Ads' ? 
                  '<line x1="4" y1="9" x2="20" y2="9"></line><line x1="4" y1="15" x2="20" y2="15"></line><line x1="10" y1="3" x2="8" y2="21"></line><line x1="16" y1="3" x2="14" y2="21"></line>' : 
                section.title === 'Interesses para Meta Ads' ? 
                  '<circle cx="12" cy="12" r="10"></circle><circle cx="12" cy="12" r="6"></circle><circle cx="12" cy="12" r="2"></circle>' : 
                section.title === 'Dados Demográficos' ? 
                  '<path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path><circle cx="12" cy="10" r="3"></circle>' : 
                section.title === 'Funis e Ofertas' ? 
                  '<path d="M12 2L2 7l10 5 10-5-10-5z"></path><path d="M2 17l10 5 10-5"></path><path d="M2 12l10 5 10-5"></path>' : 
                section.title === 'Dicas de SEO' ? 
                  '<circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line>' : 
                  '<path d="M23 6l-9.5 9.5-5-5L1 18"></path><path d="M17 6h6v6"></path>'}
              </svg>
              <h3 class="section-title">${section.title}</h3>
            </div>
            <div>
              ${section.content.split('\n').filter(Boolean).map(line => {
                const cleanLine = line.replace(/\*\*/g, '').trim();
                const isHeading = cleanLine.includes(':') && !cleanLine.includes(',');
                return isHeading ? 
                  `<p class="heading">${cleanLine}</p>` : 
                  `<p class="content">${cleanLine}</p>`;
              }).join('')}
            </div>
          </div>
        `).join('')}
      </body>
      </html>
    `;

    // Usar srcdoc em vez de document.write
    requestAnimationFrame(() => {
	  try {
		iframe.srcdoc = printContent;
	  } catch (e) {
		console.warn('Erro ao setar srcdoc:', e);
		document.body.removeChild(iframe);
		setExportError('Erro ao preparar impressão. Tente novamente.');
	  }
	});
    
iframe.onload = () => {
  try {
    const iframeWindow = iframe.contentWindow;
    if (!iframeWindow || !iframeWindow.document || !document.body.contains(iframe)) {
      throw new Error('Iframe indisponível ou removido prematuramente');
    }

    setTimeout(() => {
      try {
        iframeWindow.focus();
        iframeWindow.print();
      } catch (printError) {
        console.error('Erro ao tentar imprimir:', printError);
      }

      setTimeout(() => {
        if (document.body.contains(iframe)) {
          document.body.removeChild(iframe);
        }
        onAfterExport?.();
      }, 1000);
    }, 500);
  } catch (e) {
    console.error('Erro na preparação da impressão:', e);
    if (document.body.contains(iframe)) document.body.removeChild(iframe);
    setExportError('Erro ao preparar impressão. Tente novamente.');
  }
};
    
  } catch (error) {
    console.error('Print setup error:', error);
    setExportError(error instanceof Error ? error.message : 'Falha ao configurar a impressão.');
  }
}, [sections, selectedHeaderImage, onAfterExport]);

  const exportToPDF = useCallback(async () => {
    if (exportingRef.current) return;
    
    try {
      exportingRef.current = true;
      setIsExporting(true);
      setExportError('');
      
      const pdf = new jsPDF({
        orientation: 'p',
        unit: 'mm',
        format: 'a4'
      });

      const margin = 15;
      const lineHeight = 6;
      const pageWidth = pdf.internal.pageSize.getWidth() - 2 * margin;
      let yPosition = margin;

      // Adicionar imagem de cabeçalho
      try {
        const img = new Image();
        img.crossOrigin = 'Anonymous';
        img.src = selectedHeaderImage;
        
        await new Promise<void>((resolve, reject) => {
          img.onload = () => resolve();
          img.onerror = () => reject(new Error('Erro ao carregar imagem'));
          setTimeout(() => reject(new Error('Timeout ao carregar imagem')), 5000);
        });

        pdf.addImage(img, 'JPEG', margin, yPosition, pageWidth, (pageWidth * 9) / 16);
        yPosition += (pageWidth * 9) / 16 + 10;
      } catch (imgError) {
        console.warn('Usando PDF sem imagem:', imgError);
        yPosition += 10;
      }

      // Adicionar título
      pdf.setFontSize(20);
      pdf.setTextColor(44, 62, 80);
      pdf.text('Relatório de Segmentação Aura AI', margin, yPosition);
      yPosition += lineHeight + 5;

      // Adicionar data
      pdf.setFontSize(10);
      pdf.setTextColor(128, 128, 128);
      const date = new Date().toLocaleDateString('pt-BR');
      const time = new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
      pdf.text(`Gerado em ${date} às ${time}`, margin, yPosition);
      yPosition += lineHeight * 2;

      // Adicionar conteúdo
      for (const section of sections) {
        if (yPosition > 250) {
          pdf.addPage();
          yPosition = margin;
        }

        pdf.setFontSize(14);
        pdf.setTextColor(79, 70, 229);
        pdf.text(section.title, margin, yPosition);
        yPosition += lineHeight * 1.5;

        pdf.setFontSize(11);
        pdf.setTextColor(55, 65, 81);

        const lines = pdf.splitTextToSize(
          processEmojisForPDF(section.content.replace(/\*\*/g, '')),
          pageWidth
        );

        for (const line of lines) {
          if (yPosition > 280) {
            pdf.addPage();
            yPosition = margin;
          }

          if (line.includes(':') && !line.includes(',')) {
            pdf.setFont('helvetica', 'bold');
            pdf.text(line, margin, yPosition);
            pdf.setFont('helvetica', 'normal');
          } else {
            pdf.text(line, margin, yPosition);
          }

          yPosition += lineHeight;
        }

        yPosition += lineHeight;
      }

      // Salvar o PDF
      const filename = `AuraAI-${sections[0].title === CALENDAR_TITLE ? 'calendario' : 'relatorio'}-${new Date().toLocaleDateString('pt-BR').replace(/\//g, '-')}.pdf`;
      pdf.save(filename);
      
      setHasExported(true);
      
      if (onAfterExport) {
        onAfterExport();
      }
    } catch (error) {
      console.error('Export error:', error);
      setExportError(error instanceof Error ? error.message : 'Falha ao exportar PDF');
    } finally {
      exportingRef.current = false;
      setIsExporting(false);
    }
  }, [sections, selectedHeaderImage, onAfterExport]);

  const AutoRetryIndicator = () => {
    if (!isRetrying) return null;
    
    return (
      <div className="mt-4 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg p-3">
        <div className="flex items-center gap-2 mb-2">
          <RefreshCw className="h-4 w-4 text-indigo-600 dark:text-indigo-400 animate-spin" />
          <p className="text-sm font-medium text-indigo-700 dark:text-indigo-300">
            Gerando segmentação... Tentativa {retryAttempts} de {MAX_RETRY_ATTEMPTS}
          </p>
        </div>
        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5">
          <div 
            className="bg-indigo-600 dark:bg-indigo-500 h-2.5 rounded-full transition-all duration-300" 
            style={{ width: `${retryProgress}%` }}
          ></div>
        </div>
      </div>
    );
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
          {isCalendarMode ? 'Calendário de Conteúdo' : 'Recomendações de Segmentação'}
        </h2>
        <div className="flex space-x-2">
          {reconnecting ? (
            <button
              disabled
              className="flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-md opacity-70 cursor-not-allowed"
            >
              <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Verificando Sessão...
            </button>
          ) : null}
          
          {!hasExported ? (
            <button
              onClick={exportToPDF}
              disabled={isExporting || reconnecting || showLimitReached || isRetrying}
              className={`flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors ${
                (isExporting || reconnecting || isRetrying) ? 'opacity-70 cursor-not-allowed' : ''
              }`}
              aria-label="Exportar para PDF"
            >
              {isExporting ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Exportando...
                </>
              ) : (
                <>
                  <FileDown className="h-5 w-5" />
                  Exportar PDF
                </>
              )}
            </button>
          ) : (
            <button
              onClick={printDocument}
              disabled={showLimitReached || isRetrying}
              className={`flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors ${
                isRetrying ? 'opacity-70 cursor-not-allowed' : ''
              }`}
              aria-label="Imprimir documento"
            >
              <Printer className="h-5 w-5" />
              Imprimir
            </button>
          )}
        </div>
      </div>

      {exportError && (
        <div className="bg-red-50 dark:bg-red-900/20 border-l-4 border-red-400 p-4 mb-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <AlertCircle className="h-5 w-5 text-red-400" />
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700 dark:text-red-300">
                {exportError}
              </p>
              <div className="mt-2 flex space-x-3">
                <button 
                  onClick={forceRefreshSession}
                  className="text-sm font-medium text-red-700 dark:text-red-300 hover:text-red-800 dark:hover:text-red-200 flex items-center"
                >
                  <RefreshCw className="h-4 w-4 mr-1" /> Verificar sessão
                </button>
                
                <button 
                  onClick={printDocument}
                  className="text-sm font-medium text-green-700 dark:text-green-300 hover:text-green-800 dark:hover:text-green-200 flex items-center"
                >
                  <Printer className="h-4 w-4 mr-1" /> Tentar impressão
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {!allSectionsPresent && recommendations && !isRetrying && (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border-l-4 border-yellow-400 p-4 mb-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <AlertCircle className="h-5 w-5 text-yellow-400" />
            </div>
            <div className="ml-3">
              <p className="text-sm text-yellow-700 dark:text-yellow-300">
                Algumas seções podem estar incompletas. Tentando gerar novamente automaticamente...
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Indicador de retentativa automática */}
      <AutoRetryIndicator />

      <div ref={printContentRef} className="relative">
        {showLimitReached && (
          <>
            <div className="absolute inset-0 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm z-10"></div>
            <div className="absolute top-0 left-0 right-0 z-20 p-4">
              <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 max-w-4xl mx-auto text-center">
                <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Você esgotou suas consultas mensais</h3>
                <p className="text-gray-600 dark:text-gray-300 mb-4">
                  Renove seu plano para continuar usando o serviço e obter mais recomendações de segmentação.
                </p>
                <button
                  className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors"
                  onClick={() => navigate('/#pricing-section')}
                >
                  Ver planos disponíveis
                </button>
              </div>
            </div>
          </>
        )}
      
        <div className={showLimitReached ? "filter blur-sm" : ""}>
          {recommendations && (
            <div className="space-y-6" key={`content-${componentKey}`}>
              <div className="w-full rounded-lg overflow-hidden mb-6 aspect-video bg-gray-200 dark:bg-gray-700">
                <img
                  src={selectedHeaderImage}
                  alt="Estratégia de marketing digital"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>

              <div className="text-center mb-6">
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Relatório de Segmentação Aura AI</h1>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {new Date().toLocaleDateString('pt-BR')} - {new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                </p>
              </div>

              {sections.map((section, index) => (
                <div key={`${componentKey}-section-${index}`} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <section.icon className="h-6 w-6 text-indigo-600 dark:text-indigo-400" />
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                      {section.title}
                    </h3>
                  </div>
                  <div className="prose prose-indigo dark:prose-invert max-w-none">
                    {section.content.split('\n').filter(Boolean).map((line, lineIndex) => {
                      const cleanLine = line.replace(/\*\*/g, '').trim();
                      const isHeading = cleanLine.includes(':') && !cleanLine.includes(',');
                      
                      return isHeading ? (
                        <h4 key={`${componentKey}-line-${index}-${lineIndex}`} className="font-bold text-gray-800 dark:text-gray-200 mt-4 mb-2">
                          {cleanLine}
                        </h4>
                      ) : (
                        <p key={`${componentKey}-line-${index}-${lineIndex}`} className="text-gray-700 dark:text-gray-300 mb-2">
                          {cleanLine}
                        </p>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RecommendationDisplay;